import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { createGlobalStyle, keyframes } from 'styled-components';

const Fonts = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400;1,600&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap');
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(28px); }
  to { opacity: 1; transform: translateY(0); }
`;

const scaleIn = keyframes`
  from { transform: scaleX(0); }
  to { transform: scaleX(1); }
`;

// ─── Tokens ───────────────────────────────────────────────────────────────────
const navy   = '#0d1b2a';
const teal   = '#1aada0';
const tealHi = '#22c4b5';
const cream  = '#f7f3ec';
const white  = '#ffffff';
const muted  = '#8a9bb0';
const border = 'rgba(255,255,255,0.08)';

// ─── Nav ──────────────────────────────────────────────────────────────────────
const Nav = styled.nav`
  position: fixed;
  top: 0; left: 0; right: 0;
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 3.5rem;
  background: rgba(255,255,255,0.97);
  backdrop-filter: blur(16px);
  border-bottom: 1px solid #e8e2d9;

  @media (max-width: 768px) { padding: 1rem 1.5rem; }
`;

const NavLogo = styled.img`
  height: 32px;
  object-fit: contain;
  cursor: pointer;
`;

const NavLinks = styled.div`
  display: flex;
  gap: 2.5rem;
  align-items: center;

  @media (max-width: 640px) { display: none; }
`;

const NavLink = styled.button`
  background: none;
  border: none;
  font-family: 'DM Sans', sans-serif;
  font-size: 0.8125rem;
  font-weight: 500;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #4b5563;
  cursor: pointer;
  transition: color 0.2s;
  padding: 0;

  &:hover { color: ${navy}; }
`;

const NavCta = styled.button`
  background: ${teal};
  color: ${white};
  border: none;
  font-family: 'DM Sans', sans-serif;
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 0.625rem 1.375rem;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover { background: ${tealHi}; }
`;

// ─── Hero ─────────────────────────────────────────────────────────────────────
const HeroSection = styled.section`
  min-height: 100vh;
  background: ${navy};
  display: grid;
  grid-template-columns: 1fr 1fr;
  overflow: hidden;
  position: relative;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const HeroLeft = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 10rem 3.5rem 5rem;
  position: relative;
  z-index: 2;

  @media (max-width: 768px) {
    padding: 8rem 1.75rem 3rem;
  }
`;

const SectionNumber = styled.span`
  font-family: 'DM Sans', sans-serif;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${teal};
  display: flex;
  align-items: center;
  gap: 0.875rem;
  margin-bottom: 2.5rem;
  animation: ${fadeUp} 0.7s ease both;

  &::after {
    content: '';
    display: block;
    width: 48px;
    height: 1px;
    background: ${teal};
    animation: ${scaleIn} 0.6s ease 0.3s both;
    transform-origin: left;
  }
`;

const HeroHeadline = styled.h1`
  font-family: 'Cormorant Garamond', serif;
  font-size: clamp(3rem, 5vw, 5rem);
  font-weight: 300;
  line-height: 1.1;
  color: ${white};
  margin-bottom: 1.75rem;
  animation: ${fadeUp} 0.8s ease 0.15s both;

  em {
    font-style: italic;
    color: ${teal};
  }
`;

const HeroSub = styled.p`
  font-family: 'DM Sans', sans-serif;
  font-size: 1rem;
  font-weight: 300;
  color: ${muted};
  line-height: 1.75;
  max-width: 420px;
  margin-bottom: 2.75rem;
  animation: ${fadeUp} 0.8s ease 0.25s both;
`;

const HeroActions = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  animation: ${fadeUp} 0.8s ease 0.35s both;
`;

const BtnPrimary = styled.button`
  background: ${teal};
  color: ${white};
  border: none;
  font-family: 'DM Sans', sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  padding: 0.875rem 2rem;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s, transform 0.15s;
  display: flex;
  align-items: center;
  gap: 0.625rem;

  &:hover { background: ${tealHi}; transform: translateY(-1px); }

  svg { width: 16px; height: 16px; }
`;

const BtnGhost = styled.button`
  background: transparent;
  color: rgba(255,255,255,0.7);
  border: 1px solid rgba(255,255,255,0.2);
  font-family: 'DM Sans', sans-serif;
  font-size: 0.875rem;
  font-weight: 500;
  letter-spacing: 0.04em;
  padding: 0.875rem 2rem;
  border-radius: 4px;
  cursor: pointer;
  transition: border-color 0.2s, color 0.2s, transform 0.15s;

  &:hover {
    border-color: rgba(255,255,255,0.5);
    color: ${white};
    transform: translateY(-1px);
  }
`;

const CredentialsBar = styled.div`
  margin-top: 3.5rem;
  padding-top: 2rem;
  border-top: 1px solid ${border};
  display: flex;
  gap: 2rem;
  flex-wrap: wrap;
  animation: ${fadeUp} 0.8s ease 0.45s both;
`;

const Credential = styled.div`
  font-family: 'DM Sans', sans-serif;
  font-size: 0.7rem;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(255,255,255,0.3);
`;

const HeroRight = styled.div`
  position: relative;
  overflow: hidden;

  @media (max-width: 900px) { display: none; }
`;

const HeroImage = styled.div`
  position: absolute;
  inset: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    filter: brightness(0.45) saturate(0.9);
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, ${navy} 0%, transparent 40%);
  }
`;

const HeroAccent = styled.div`
  position: absolute;
  bottom: 3rem;
  right: 3rem;
  width: 200px;
  background: rgba(26,173,160,0.12);
  border: 1px solid rgba(26,173,160,0.3);
  border-radius: 8px;
  padding: 1.25rem 1.5rem;
  backdrop-filter: blur(8px);
  z-index: 2;
`;

const AccentLabel = styled.div`
  font-family: 'DM Sans', sans-serif;
  font-size: 0.65rem;
  font-weight: 600;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: ${teal};
  margin-bottom: 0.5rem;
`;

const AccentValue = styled.div`
  font-family: 'Cormorant Garamond', serif;
  font-size: 2.25rem;
  font-weight: 600;
  color: ${white};
  line-height: 1;
`;

const AccentSub = styled.div`
  font-family: 'DM Sans', sans-serif;
  font-size: 0.72rem;
  color: rgba(255,255,255,0.45);
  margin-top: 0.25rem;
`;

// ─── Products ─────────────────────────────────────────────────────────────────
const ProductsSection = styled.section`
  background: ${cream};
  padding: 7rem 3.5rem;

  @media (max-width: 768px) { padding: 5rem 1.75rem; }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: baseline;
  gap: 1.5rem;
  margin-bottom: 3.5rem;
`;

const SectionNum = styled.span`
  font-family: 'Cormorant Garamond', serif;
  font-size: 4rem;
  font-weight: 300;
  color: rgba(13,27,42,0.1);
  line-height: 1;
  flex-shrink: 0;
`;

const SectionTitle = styled.h2`
  font-family: 'Cormorant Garamond', serif;
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 400;
  color: ${navy};
  line-height: 1.15;

  em {
    font-style: italic;
    color: ${teal};
  }
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const ProductCard = styled.div<{ dark?: boolean }>`
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.25s, box-shadow 0.25s;
  background: ${p => p.dark ? navy : white};
  border: 1px solid ${p => p.dark ? 'transparent' : '#e8e2d9'};
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 24px 48px rgba(13,27,42,${p => p.dark ? '0.4' : '0.12'});
  }
`;

const CardImage = styled.div`
  height: 220px;
  overflow: hidden;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
  }

  ${ProductCard}:hover & img { transform: scale(1.04); }
`;

const CardImageOverlay = styled.div<{ dark?: boolean }>`
  position: absolute;
  inset: 0;
  background: ${p => p.dark
    ? 'linear-gradient(180deg, rgba(13,27,42,0.1) 0%, rgba(13,27,42,0.5) 100%)'
    : 'linear-gradient(180deg, transparent 60%, rgba(247,243,236,0.3) 100%)'};
`;

const CardTag = styled.div`
  position: absolute;
  top: 1rem;
  left: 1rem;
  background: ${teal};
  color: ${white};
  font-family: 'DM Sans', sans-serif;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 0.3rem 0.75rem;
  border-radius: 2px;
`;

const CardBody = styled.div<{ dark?: boolean }>`
  padding: 2rem;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const CardTitle = styled.h3<{ dark?: boolean }>`
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.625rem;
  font-weight: 600;
  color: ${p => p.dark ? white : navy};
  margin-bottom: 0.75rem;
  line-height: 1.2;
`;

const CardDesc = styled.p<{ dark?: boolean }>`
  font-family: 'DM Sans', sans-serif;
  font-size: 0.875rem;
  font-weight: 300;
  line-height: 1.7;
  color: ${p => p.dark ? 'rgba(255,255,255,0.55)' : '#4b5563'};
  flex: 1;
  margin-bottom: 1.75rem;
`;

const CardPoints = styled.ul<{ dark?: boolean }>`
  list-style: none;
  padding: 0;
  margin: 0 0 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const CardPoint = styled.li<{ dark?: boolean }>`
  font-family: 'DM Sans', sans-serif;
  font-size: 0.8125rem;
  color: ${p => p.dark ? 'rgba(255,255,255,0.5)' : '#6b7280'};
  padding-left: 1.125rem;
  position: relative;

  &::before {
    content: '—';
    position: absolute;
    left: 0;
    color: ${teal};
    font-weight: 600;
  }
`;

const CardCta = styled.button<{ dark?: boolean }>`
  align-self: flex-start;
  background: ${p => p.dark ? teal : navy};
  color: ${white};
  border: none;
  font-family: 'DM Sans', sans-serif;
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover { opacity: 0.85; }
`;

// ─── Features ─────────────────────────────────────────────────────────────────
const FeaturesSection = styled.section`
  background: ${white};
  padding: 7rem 3.5rem;

  @media (max-width: 768px) { padding: 5rem 1.75rem; }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.25rem;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 500px) { grid-template-columns: 1fr; }
`;

const FeatureCard = styled.div`
  border: 1px solid #ede9e2;
  border-radius: 10px;
  overflow: hidden;
  transition: border-color 0.2s, transform 0.2s;

  &:hover {
    border-color: ${teal};
    transform: translateY(-3px);
  }
`;

const FeatureImg = styled.img`
  width: 100%;
  height: 140px;
  object-fit: cover;
  display: block;
`;

const FeatureBody = styled.div`
  padding: 1.375rem;
`;

const FeatureTitle = styled.h4`
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.1rem;
  font-weight: 600;
  color: ${navy};
  margin-bottom: 0.5rem;
`;

const FeatureDesc = styled.p`
  font-family: 'DM Sans', sans-serif;
  font-size: 0.8rem;
  color: #6b7280;
  line-height: 1.65;
`;

// ─── Footer ───────────────────────────────────────────────────────────────────
const Footer = styled.footer`
  background: ${navy};
  padding: 3.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1.5rem;

  @media (max-width: 768px) { padding: 2.5rem 1.75rem; flex-direction: column; align-items: flex-start; }
`;

const FooterLogo = styled.img`
  height: 28px;
  object-fit: contain;
  opacity: 0.8;
`;

const FooterLinks = styled.div`
  display: flex;
  gap: 2rem;
`;

const FooterLink = styled.button`
  background: none;
  border: none;
  font-family: 'DM Sans', sans-serif;
  font-size: 0.78rem;
  color: rgba(255,255,255,0.4);
  cursor: pointer;
  transition: color 0.2s;
  padding: 0;

  &:hover { color: rgba(255,255,255,0.8); }
`;

const FooterCopy = styled.p`
  font-family: 'DM Sans', sans-serif;
  font-size: 0.75rem;
  color: rgba(255,255,255,0.25);
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

// ─── Component ────────────────────────────────────────────────────────────────
const features = [
  {
    title: 'Fast KYC / AML',
    desc: 'Automated checks, human review, and full compliance trail in a single flow.',
    img: 'https://plus.unsplash.com/premium_photo-1661549683908-b11e9855c469?auto=format&fit=crop&w=600&q=80',
  },
  {
    title: 'Multi-Currency Banking',
    desc: 'Settlement accounts across major currencies for global merchant operations.',
    img: 'https://images.unsplash.com/photo-1580519542036-c47de6196ba5?auto=format&fit=crop&w=600&q=80',
  },
  {
    title: 'Document Verification',
    desc: 'ID, corporate docs, and address proofs verified with accuracy and speed.',
    img: 'https://images.unsplash.com/photo-1521791055366-0d553872125f?auto=format&fit=crop&w=600&q=80',
  },
  {
    title: 'Real-Time Status',
    desc: 'Live application tracking with automated status updates at every stage.',
    img: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=600&q=80',
  },
];

const HomePage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <>
      <Fonts />

      {/* Nav */}
      <Nav>
        <NavLogo
          src="https://fctech.cc/wp-content/uploads/2025/09/FinaConnect-Logo-2.png"
          alt="FinaConnect"
          onClick={() => navigate('/')}
        />
        <NavLinks>
          <NavLink onClick={() => navigate('/register')}>Onboarding</NavLink>
          <NavLink onClick={() => navigate('/training')}>AML Training</NavLink>
          <NavLink onClick={() => window.location.href = 'mailto:support@finaconnect.com'}>Contact</NavLink>
          <NavCta onClick={() => navigate('/register')}>Apply Now</NavCta>
        </NavLinks>
      </Nav>

      {/* Hero */}
      <HeroSection>
        <HeroLeft>
          <SectionNumber>01 &nbsp; FinaConnect</SectionNumber>

          <HeroHeadline>
            Your Partner To<br />
            Unlock <em>Value</em><br />
            In Compliance.
          </HeroHeadline>

          <HeroSub>
            Streamlined merchant onboarding, AML compliance training,
            and payment solutions — built for regulated businesses in the UK.
          </HeroSub>

          <HeroActions>
            <BtnPrimary onClick={() => navigate('/register')}>
              Start Application
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 8h10M9 4l4 4-4 4"/>
              </svg>
            </BtnPrimary>
            <BtnGhost onClick={() => navigate('/training')}>
              AML Training Programme
            </BtnGhost>
          </HeroActions>

          <CredentialsBar>
            <Credential>Regulation 24</Credential>
            <Credential>MLR 2017</Credential>
            <Credential>UK-Regulated</Credential>
            <Credential>AML Compliant</Credential>
          </CredentialsBar>
        </HeroLeft>

        <HeroRight>
          <HeroImage>
            <img
              src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1200&q=80"
              alt="Financial district"
            />
          </HeroImage>
          <HeroAccent>
            <AccentLabel>Programme</AccentLabel>
            <AccentValue>8</AccentValue>
            <AccentSub>Week AML Training</AccentSub>
          </HeroAccent>
        </HeroRight>
      </HeroSection>

      {/* Products */}
      <ProductsSection>
        <Container>
          <SectionHeader>
            <SectionNum>02</SectionNum>
            <SectionTitle>
              Two products.<br />
              <em>One platform.</em>
            </SectionTitle>
          </SectionHeader>

          <ProductsGrid>
            {/* Merchant Onboarding */}
            <ProductCard onClick={() => navigate('/register')}>
              <CardImage>
                <img
                  src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=800&q=80"
                  alt="Merchant Onboarding"
                />
                <CardImageOverlay />
                <CardTag>Merchant Onboarding</CardTag>
              </CardImage>
              <CardBody>
                <CardTitle>Global Merchant Onboarding</CardTitle>
                <CardDesc>
                  A streamlined application process for merchants seeking payment
                  acceptance, multi-currency settlement, and full KYC/KYB verification.
                </CardDesc>
                <CardPoints>
                  <CardPoint>Company & beneficial ownership verification</CardPoint>
                  <CardPoint>Multi-currency settlement accounts</CardPoint>
                  <CardPoint>Real-time application tracking</CardPoint>
                </CardPoints>
                <CardCta>Start Application</CardCta>
              </CardBody>
            </ProductCard>

            {/* AML Training */}
            <ProductCard dark onClick={() => navigate('/training')}>
              <CardImage>
                <img
                  src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800&q=80"
                  alt="AML Training"
                />
                <CardImageOverlay dark />
                <CardTag>AML Compliance</CardTag>
              </CardImage>
              <CardBody dark>
                <CardTitle dark>AML Training Programme</CardTitle>
                <CardDesc dark>
                  An 8-week mandatory training programme under Regulation 24 of the
                  Money Laundering Regulations 2017, built specifically for payments
                  and acquiring teams.
                </CardDesc>
                <CardPoints dark>
                  <CardPoint dark>Covers ML, TF & proliferation financing</CardPoint>
                  <CardPoint dark>MCQ knowledge checks + reflective questions</CardPoint>
                  <CardPoint dark>Manager review dashboard & audit trail</CardPoint>
                </CardPoints>
                <CardCta dark>View Programme</CardCta>
              </CardBody>
            </ProductCard>
          </ProductsGrid>
        </Container>
      </ProductsSection>

      {/* Features */}
      <FeaturesSection>
        <Container>
          <SectionHeader>
            <SectionNum>03</SectionNum>
            <SectionTitle>
              Platform<br />
              <em>Capabilities</em>
            </SectionTitle>
          </SectionHeader>

          <FeaturesGrid>
            {features.map((f) => (
              <FeatureCard key={f.title}>
                <FeatureImg src={f.img} alt={f.title} />
                <FeatureBody>
                  <FeatureTitle>{f.title}</FeatureTitle>
                  <FeatureDesc>{f.desc}</FeatureDesc>
                </FeatureBody>
              </FeatureCard>
            ))}
          </FeaturesGrid>
        </Container>
      </FeaturesSection>

      {/* Footer */}
      <Footer>
        <FooterLogo
          src="https://fctech.cc/wp-content/uploads/2025/09/FinaConnect-Logo-1-1-e1758799361419.png"
          alt="FinaConnect"
        />
        <FooterLinks>
          <FooterLink onClick={() => navigate('/terms')}>Terms</FooterLink>
          <FooterLink onClick={() => navigate('/privacy')}>Privacy</FooterLink>
          <FooterLink onClick={() => navigate('/compliance-policy')}>Compliance</FooterLink>
        </FooterLinks>
        <FooterCopy>© {new Date().getFullYear()} FinaConnect. All rights reserved.</FooterCopy>
      </Footer>
    </>
  );
};

export default HomePage;
